import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Clock3, ExternalLink, MessageCircle, Ship } from "lucide-react";
import { api, mediaUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const FALLBACK_BOAT_IMAGE =
  "https://images.unsplash.com/photo-1620720631381-7a8f05654877?crop=entropy&cs=srgb&fm=jpg&q=85";

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(parsed);
};

const selectCls =
  "w-full bg-white border border-[#E5DED4] focus:border-[#9A7B66] rounded-md px-4 py-3 text-sm font-sans text-[#2C2825] outline-none transition-colors appearance-none cursor-pointer";

export default function BoatSchedule({ phone, settings }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [route, setRoute] = useState("outbound");
  const [company, setCompany] = useState("");
  const [date, setDate] = useState(todayIso());

  useEffect(() => {
    api
      .get("/boat-timetable")
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const operators = data?.lembongan_operators || [];
  const companiesForRoute = useMemo(
    () =>
      operators.filter((op) =>
        route === "outbound" ? (op.sanur_departures || []).length > 0 : (op.lembongan_departures || []).length > 0
      ),
    [operators, route]
  );
  const selected = companiesForRoute.find((op) => op.operator === company) || null;
  const times = selected ? (route === "outbound" ? selected.sanur_departures : selected.lembongan_departures) : [];

  const boatImages = useMemo(() => {
    try {
      return JSON.parse(settings?.boat_company_images || "{}");
    } catch {
      return {};
    }
  }, [settings?.boat_company_images]);
  const boatImage = selected ? boatImages[selected.operator] || FALLBACK_BOAT_IMAGE : FALLBACK_BOAT_IMAGE;

  const fromStation = route === "outbound" ? selected?.sanur_station || "Sanur Harbour" : selected?.lembongan_station || "Nusa Lembongan";
  const toStation = route === "outbound" ? selected?.lembongan_station || "Nusa Lembongan" : selected?.sanur_station || "Sanur Harbour";

  const whatsappDigits = (phone || "").replace(/[^0-9]/g, "");
  const whatsappText = selected
    ? `Hello Nusadira Lembongan Villa, could you please help me book ${selected.operator} from ${fromStation} to ${toStation} on ${formatDate(date)}?`
    : "Hello Nusadira Lembongan Villa, could you please help me book fast boat tickets between Sanur and Nusa Lembongan?";
  const whatsappUrl = whatsappDigits ? `https://wa.me/${whatsappDigits}?text=${encodeURIComponent(whatsappText)}` : "";

  const updatedAt = data?.fetched_at
    ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(
        new Date(data.fetched_at)
      )
    : "";
  const sourceUrl = data?.lembongan_source_url || data?.source_url || "https://sanurharbour.com/fast-boat-nusa-lembongan/";

  return (
    <section id="boat-schedule" data-testid="boat-schedule-section" className="py-24 sm:py-32 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.25em] font-sans font-medium text-[#9A7B66] mb-5"
        >
          {t("schedule.eyebrow")}
        </motion.p>

        <div className="grid lg:grid-cols-[1fr_420px] gap-8 lg:gap-12 items-end mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t("schedule.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-sm text-[#6E675F] font-sans leading-relaxed"
          >
            {t("schedule.intro")}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="bg-[#FBF9F5] border border-[#E5DED4] rounded-md overflow-hidden shadow-[0_20px_60px_rgba(44,40,37,0.06)]"
        >
          {loading && (
            <div data-testid="boat-schedule-loading" className="grid md:grid-cols-3 gap-5 px-5 sm:px-7 py-7">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 rounded-md bg-[#E9E1D7] animate-pulse" />
              ))}
            </div>
          )}

          {!loading && error && (
            <div data-testid="boat-schedule-error" className="px-5 sm:px-7 py-12 text-center">
              <p className="font-serif text-2xl text-[#2C2825]">{t("schedule.error")}</p>
              <a
                data-testid="boat-schedule-error-source-link"
                href="https://sanurharbour.com/fast-boat-nusa-lembongan/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-sm font-sans font-medium text-[#9A7B66] hover:text-[#826552]"
              >
                {t("schedule.source")} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {!loading && !error && !operators.length && (
            <p data-testid="boat-schedule-empty" className="px-5 sm:px-7 py-10 text-sm text-[#6E675F] font-sans">
              {t("schedule.empty")}
            </p>
          )}

          {!loading && !error && operators.length > 0 && (
            <>
              <div className="grid md:grid-cols-3 gap-4 px-5 sm:px-7 py-7 border-b border-[#E5DED4]">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#9A7B66] mb-2">
                    {t("schedule.stepRoute")}
                  </label>
                  <select
                    data-testid="boat-route-filter"
                    value={route}
                    onChange={(e) => {
                      setRoute(e.target.value);
                      setCompany("");
                    }}
                    className={selectCls}
                  >
                    <option value="outbound">{t("schedule.fromSanur")}</option>
                    <option value="inbound">{t("schedule.fromLembongan")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#9A7B66] mb-2">
                    {t("schedule.stepCompany")}
                  </label>
                  <select
                    data-testid="boat-company-filter"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">{t("schedule.chooseCompany")}</option>
                    {companiesForRoute.map((op) => (
                      <option key={op.operator} value={op.operator}>
                        {op.operator}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.16em] font-sans font-semibold text-[#9A7B66] mb-2">
                    {t("schedule.stepDate")}
                  </label>
                  <input
                    data-testid="boat-date-filter"
                    type="date"
                    value={date}
                    min={todayIso()}
                    onChange={(e) => setDate(e.target.value)}
                    className={selectCls}
                  />
                </div>
              </div>

              {selected ? (
                <div data-testid="boat-result-card" className="grid md:grid-cols-[380px_1fr]">
                  <div className="relative h-56 md:h-full min-h-[224px]">
                    <img
                      data-testid="boat-result-image"
                      src={mediaUrl(boatImage)}
                      alt={`${selected.operator} fast boat`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                      <div>
                        <h3 data-testid="boat-result-company" className="font-serif text-2xl sm:text-3xl text-[#2C2825] leading-tight">
                          {selected.operator}
                        </h3>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#9A7B66] font-sans font-medium">
                          {selected.class}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFE8DE] px-3 py-1.5 text-[11px] font-sans font-medium text-[#6E675F] whitespace-nowrap">
                        <Clock3 className="w-3.5 h-3.5 text-[#9A7B66]" /> {selected.duration}
                      </span>
                      {selected.overridden && (
                        <span data-testid="boat-manual-badge" className="inline-flex items-center rounded-full bg-[#9A7B66]/10 px-3 py-1.5 text-[11px] font-sans font-semibold text-[#9A7B66] whitespace-nowrap">
                          {t("schedule.manual")}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-sans text-[#6E675F] mb-6">
                      <span className="inline-flex items-center gap-2">
                        {fromStation} <ArrowRight className="w-3.5 h-3.5 text-[#9A7B66]" /> {toStation}
                      </span>
                      <span data-testid="boat-result-date" className="inline-flex items-center gap-2 text-[#2C2825]">
                        <CalendarDays className="w-4 h-4 text-[#9A7B66]" /> {formatDate(date)}
                      </span>
                    </div>

                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#9A7B66] font-sans font-semibold mb-3">
                      {t("schedule.departures")}
                    </p>
                    <div data-testid="boat-result-times" className="flex flex-wrap gap-2 mb-7">
                      {times.map((time) => (
                        <span
                          key={time}
                          className="rounded-full border border-[#D8CBB8] bg-white px-4 py-2 text-sm font-serif text-[#2C2825]"
                        >
                          {time}
                        </span>
                      ))}
                    </div>

                    {whatsappUrl && (
                      <a
                        data-testid="boat-booking-whatsapp-button"
                        href={whatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9A7B66] px-6 py-3 text-[11px] font-sans font-semibold uppercase tracking-[0.14em] text-white hover:bg-[#826552] transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> {t("schedule.bookCta")}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div data-testid="boat-result-placeholder" className="px-5 sm:px-7 py-12 flex flex-col items-center text-center gap-3">
                  <span className="w-12 h-12 rounded-full bg-[#9A7B66]/10 text-[#9A7B66] flex items-center justify-center">
                    <Ship className="w-5 h-5" />
                  </span>
                  <p className="text-sm font-sans text-[#A0988E]">{t("schedule.placeholder")}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-7 py-4 border-t border-[#E5DED4] bg-white/45">
                <p data-testid="boat-schedule-updated" className="text-[11px] uppercase tracking-[0.16em] text-[#A0988E] font-sans">
                  {t("schedule.updated")}: {updatedAt}
                  {data?.stale ? ` · ${t("schedule.stale")}` : ""}
                </p>
                <div className="flex items-center gap-5">
                  <p className="text-xs font-sans text-[#A0988E]">{t("schedule.disclaimer")}</p>
                  <a
                    data-testid="boat-schedule-source-link"
                    href={sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-sans font-semibold text-[#9A7B66] hover:text-[#826552] transition-colors"
                  >
                    {t("schedule.source")} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
}
